import type { ReactNode, Context } from "react";
import { createContext, useContext, useSyncExternalStore, useRef, useCallback, useId } from "react";
import { useSyncExternalStoreWithSelector } from "use-sync-external-store/with-selector";

/**
 * Store state in React Context and subscribe to selected properties using a similar api as useState.
 * Optimized to only re-render when the selected property changes.
 *
 * eg.
 * const [value, setValue] = useCustomContext<'value'>('value')
 */

export interface ContextStateProps<T> {
  id: string;
  state: T;
}

type ContextState<T> = Context<ContextStateProps<T>>;

/**
 * Context provider
 * Adds a unique id to the context to avoid event name collisions
 */

function ContextStateProvider<T>({
  contextState,
  children,
  defaultState,
}: {
  contextState: ContextState<T>;
  children: ReactNode;
  defaultState: T;
}) {
  const id = useId();
  const ref = useRef<{ id: string; state: T }>({
    state: typeof structuredClone !== "undefined" ? structuredClone(defaultState) : defaultState,
    id,
  });
  return <contextState.Provider value={ref.current}>{children}</contextState.Provider>;
}

/**
 * Returns a function that updates state for a specific key
 * You can pass the next state directly, or a function that calculates it from the previous state.
 *
 * Esepcially useful when not wanting to subscribe to any state changes.
 */
type UseSetCustomContextState<T> = <K extends keyof T>(key: K) => SetContextStateReturn<T[K]>;

type SetContextStateReturn<S, P = S> = (value: S | ((prevValue: P) => S)) => void;

function useSetContextState<T, K extends keyof T>(contextState: ContextState<T>, key: K): SetContextStateReturn<T[K]> {
  const context = useContext(contextState);
  return useCallback(
    (value: T[K] | ((prevValue: T[K]) => T[K])) => {
      let nextValue: T[K];
      if (typeof value === "function") {
        // pass in the prevValue to generate the next value
        nextValue = (value as (prevValue: T[K]) => T[K])(context.state[key]);
      } else {
        nextValue = value as T[K];
      }
      const eventName = String(key) + context.id;
      context.state[key] = nextValue;
      const event = new CustomEvent(eventName, { detail: nextValue });
      global.window?.dispatchEvent?.(event);
    },
    [key],
  );
}

/**
 * Returns a function that updates partial state
 * You can pass the next state directly, or a function that calculates it from the previous state.
 *
 * Especially useful when not wanting to subscribe to any state changes.
 */
type UseSetCustomContextStatePartial<T> = () => SetContextStateReturn<Partial<T>, T>;

// When no key, set partial state
function useSetContextStatePartial<T>(contextState: ContextState<T>): SetContextStateReturn<Partial<T>, T> {
  const context = useContext(contextState);
  return useCallback((value: Partial<T> | ((prevState: T) => Partial<T>)) => {
    let nextValue = value;
    if (typeof value === "function") {
      // pass in the prev state object to generate a new state
      nextValue = (value as (prevState: T) => Partial<T>)(context.state);
    }
    // trigger updates
    if (typeof nextValue === "object") {
      for (const k in nextValue) {
        const eventName = String(k) + context.id;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        context.state[k] = nextValue[k]!;
        const event = new CustomEvent(eventName, { detail: nextValue[k] });
        global.window?.dispatchEvent?.(event);
      }
    }
  }, []);
}

/**
 * Returns a reactive value and its setValue function from the context state as a tuple, similar to useState
 * You can pass the next state directly, or a function that calculates it from the previous state.
 * eg.
 * const [value, setValue] = useCustomContext('value')
 *
 */
type UseCustomContextState<T> = <K extends keyof T>(key: K) => ContextStateReturn<T[K]>;

type ContextStateReturn<S> = [value: S, set: (value: S | ((prevValue: S) => S)) => void];

function useContextState<T, K extends keyof T>(contextState: ContextState<T>, key: K): ContextStateReturn<T[K]> {
  const context = useContext(contextState);
  const eventName = String(key) + context.id;
  const set = useCallback(
    (value: T[K] | ((prevValue: T[K]) => T[K])) => {
      let detail = value;
      if (typeof value === "function") {
        // pass in the prevValue to generate the next value
        detail = (value as (prevValue: T[K]) => T[K])(context.state[key]);
        context.state[key] = detail;
      } else {
        context.state[key] = value;
      }
      const event = new CustomEvent(eventName, { detail });
      global.window?.dispatchEvent(event);
    },
    [key, eventName],
  );

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      global.window?.addEventListener(eventName, onStoreChange);
      return () => global.window?.removeEventListener(eventName, onStoreChange);
    },
    [key, eventName],
  );
  const value = useSyncExternalStore<T[K]>(
    subscribe,
    () => context.state?.[key] as T[K],
    () => context.state?.[key] as T[K], // satisfies server snapshot
  );

  return [value, set];
}

/**
 * Derives a value from one or more state keys, re-rendering only when the derived value
 * changes — not on every update to those keys.
 * Use when many components share state but each cares about a small slice.
 *
 * `keys` MUST be a stable reference (module scope or memoized) — it drives the subscription,
 * so a new array each render resubscribes every render.
 */
type UseCustomContextStateSelector<T> = <R>(
  keys: readonly (keyof T)[],
  selector: (state: T) => R,
  isEqual?: (a: R, b: R) => boolean,
) => R;

function useContextStateSelector<T, R>(
  contextState: ContextState<T>,
  keys: readonly (keyof T)[],
  selector: (state: T) => R,
  isEqual?: (a: R, b: R) => boolean,
): R {
  const context = useContext(contextState);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const names = keys.map((key) => String(key) + context.id);
      names.forEach((name) => global.window?.addEventListener(name, onStoreChange));
      return () => names.forEach((name) => global.window?.removeEventListener(name, onStoreChange));
    },
    [keys, context],
  );

  // `context.state` is mutated in place, so spread a copy to give the shim a fresh snapshot
  // identity; otherwise it would never re-run the selector.
  const getSnapshot = useCallback(() => ({ ...context.state }), [context]);

  return useSyncExternalStoreWithSelector<T, R>(subscribe, getSnapshot, getSnapshot, selector, isEqual);
}

/**
 * Returns the entire state as a mutable ref. Not reactive.
 */
type UseCustomContextStateRef<T> = () => T;

function useContextStateRef<T>(contextState: ContextState<T>) {
  const context = useContext(contextState);
  return context.state;
}

/**
 *  Exported generator functions used to create custom context and hydrate provider and hooks hydrated
 *
 * eg.
 * export const CustomContext = createContextState<Init>(defaultState, "CustomContext");
 * export const CustomProvider = createContextStateProvider<Init>(CustomContext, defaultState);
 * export const useSetCustomState = createUseSetContextState<Init>(CustomContext);
 * export const useSetCustomStatePartial = createUseSetContextStatePartial<Init>(CustomContext);
 * export const useCustomStateRef = createUseContextStateRef<Init>(CustomContext);
 * const useCustomState = createUseContextState<Init>(CustomContext);
 * export default useCustomState;
 */

export function createContextState<T>(state = {} as T, displayName: Capitalize<string>) {
  const context = createContext<ContextStateProps<T>>({
    id: "",
    state,
  });
  context.displayName = displayName;

  return context;
}
export function createContextStateProvider<T>(contextState: ContextState<T>, defaultState: T) {
  return ({ children }: { children: ReactNode }) => ContextStateProvider({ contextState, defaultState, children });
}
export function createUseContextState<T>(contextState: ContextState<T>): UseCustomContextState<T> {
  return (key) => useContextState(contextState, key);
}
export function createUseSetContextState<T>(contextState: ContextState<T>): UseSetCustomContextState<T> {
  return (key) => useSetContextState(contextState, key);
}
export function createUseContextStateSelector<T>(contextState: ContextState<T>): UseCustomContextStateSelector<T> {
  return (keys, selector, isEqual) => useContextStateSelector(contextState, keys, selector, isEqual);
}
export function createUseSetContextStatePartial<T>(contextState: ContextState<T>): UseSetCustomContextStatePartial<T> {
  return () => useSetContextStatePartial(contextState);
}
export function createUseContextStateRef<T>(contextState: ContextState<T>): UseCustomContextStateRef<T> {
  return () => useContextStateRef(contextState);
}
